import React, { useState } from 'react';

const tabList = [
  { key: 'customer', label: 'Customer' },
  { key: 'location', label: 'Location' },
  { key: 'user', label: 'User' },
  { key: 'study', label: 'Study Info' },
];

// Default values for all fields except Open/Closed
const defaultPossibleValues = {
  customerNo: ['C001', 'C002'],
  customerName: ['Acme', 'Globex'],
  project: ['P001', 'P002'],
  trial: ['T001', 'T002'],
  year: ['2023', '2024', '2025'],
  contract: ['Contract A', 'Contract B', 'Contract C'],
  country: ['Denmark', 'Sweden'],
  host: ['Host1', 'Host2'],
  trialAddress: ['Copenhagen', 'Aarhus', 'Odense'],
  studyDirector: ['Director1', 'Director2'],
  principalInvestigator: ['PI1', 'PI2'],
  area: ['Area1', 'Area2'],
  studyType: ['Type1', 'Type2'],
  crop: ['Wheat', 'Barley'],
  weighedOutApplication: ['App1', 'App2'],
  fieldPhaseStart: ['Spring', 'Summer'],
};

function extractOptions(possibleValues, field) {
  // Map new endpoint fields to component fields
  switch (field) {
    case 'customerNo':
      return Array.isArray(possibleValues.Customer)
        ? possibleValues.Customer.map(c => c.customer_no)
        : defaultPossibleValues.customerNo;
    case 'customerName':
      return Array.isArray(possibleValues.Customer)
        ? possibleValues.Customer.map(c => c.customer_name)
        : defaultPossibleValues.customerName;
    case 'contract':
      return Array.isArray(possibleValues.Contract)
        ? possibleValues.Contract.map(c => c.description)
        : defaultPossibleValues.contract;
    case 'country':
      return Array.isArray(possibleValues.Country)
        ? possibleValues.Country.map(c => c.name)
        : defaultPossibleValues.country;
    case 'host':
      return Array.isArray(possibleValues.Host)
        ? possibleValues.Host.map(h => h.name)
        : defaultPossibleValues.host;
    case 'trialAddress':
      return Array.isArray(possibleValues.Host)
        ? possibleValues.Host.map(h => h.address)
        : defaultPossibleValues.trialAddress;
    case 'trial':
      return Array.isArray(possibleValues.Trial)
        ? possibleValues.Trial.map(t => t.trial_no)
        : defaultPossibleValues.trial;
    case 'fieldPhaseStart':
      return Array.isArray(possibleValues.Trial)
        ? possibleValues.Trial.map(t => t.fieldPhaseStart)
        : defaultPossibleValues.fieldPhaseStart;
    case 'weighedOutApplication':
      return Array.isArray(possibleValues.Trial)
        ? possibleValues.Trial.map(t => t.weighedApplication)
        : defaultPossibleValues.weighedOutApplication;
    case 'studyType':
      return Array.isArray(possibleValues.StudyType)
        ? possibleValues.StudyType.map(s => s.name)
        : defaultPossibleValues.studyType;
    case 'crop':
      return Array.isArray(possibleValues.Crop)
        ? possibleValues.Crop.map(c => c.name)
        : defaultPossibleValues.crop;
    case 'area':
      // Not present in new endpoint, fallback to default
      return defaultPossibleValues.area;
    case 'project':
      // Not present in new endpoint, fallback to default
      return defaultPossibleValues.project;
    case 'year':
      // Not present in new endpoint, fallback to default
      return defaultPossibleValues.year;
    case 'studyDirector':
      // Not present in new endpoint, fallback to default
      return defaultPossibleValues.studyDirector;
    case 'principalInvestigator':
      // Not present in new endpoint, fallback to default
      return defaultPossibleValues.principalInvestigator;
    default:
      return [];
  }
}

// Accept possibleValues prop for auto-complete options
export default function TrialFilter({ onOk, possibleValues = {} }) {
  const [activeTab, setActiveTab] = useState('customer');
  const [form, setForm] = useState({
    customerNo: '',
    customerName: '',
    project: '',
    trial: '',
    year: '',
    contract: '',
    openClosed: '',
    country: '',
    host: '',
    trialAddress: '',
    studyDirector: '',
    principalInvestigator: '',
    area: '',
    studyType: '',
    crop: '',
    weighedOutApplication: '',
    fieldPhaseStart: '',
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Helper to render dropdown with empty "" option as default
  const renderDropdown = (name, options, label, defaultLabel = "Select") => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <select
        name={name}
        value={form[name]}
        onChange={handleChange}
        className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700"
      >
        <option value="">{defaultLabel} {label}</option>
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="w-full max-w-xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow p-4">
      {/* Tabs */}
      <div className="flex border-b mb-4">
        {tabList.map(tab => (
          <button
            key={tab.key}
            className={`px-4 py-2 font-semibold ${
              activeTab === tab.key
                ? 'border-b-2 border-emerald-600 text-emerald-700 dark:text-emerald-400'
                : 'text-gray-600 dark:text-gray-300'
            }`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="mb-6">
        {activeTab === 'customer' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {renderDropdown('customerNo', extractOptions(possibleValues, 'customerNo'), 'Customer No')}
            {renderDropdown('customerName', extractOptions(possibleValues, 'customerName'), 'Customer Name')}
            {renderDropdown('project', extractOptions(possibleValues, 'project'), 'Project')}
            {renderDropdown('trial', extractOptions(possibleValues, 'trial'), 'Trial')}
            <div>
              <label className="block text-sm font-medium mb-1">Year</label>
              <input
                type="text"
                name="year"
                value={form.year}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700"
                placeholder="Year"
                list="year-options"
                autoComplete="off"
              />
              <datalist id="year-options">
                {extractOptions(possibleValues, 'year').map(opt => (
                  <option key={opt} value={opt} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Contract</label>
              <input
                type="text"
                name="contract"
                value={form.contract}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700"
                placeholder="Contract"
                list="contract-options"
                autoComplete="off"
              />
              <datalist id="contract-options">
                {extractOptions(possibleValues, 'contract').map(opt => (
                  <option key={opt} value={opt} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Open/Closed</label>
              <select
                name="openClosed"
                value={form.openClosed}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700"
              >
                <option value="">All</option>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
        )}
        {activeTab === 'location' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {renderDropdown('country', extractOptions(possibleValues, 'country'), 'Country')}
            {renderDropdown('host', extractOptions(possibleValues, 'host'), 'Host')}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium mb-1">Trial Address</label>
              <input
                type="text"
                name="trialAddress"
                value={form.trialAddress}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700"
                placeholder="Trial Address"
                list="trialAddress-options"
                autoComplete="off"
              />
              <datalist id="trialAddress-options">
                {extractOptions(possibleValues, 'trialAddress').map(opt => (
                  <option key={opt} value={opt} />
                ))}
              </datalist>
            </div>
          </div>
        )}
        {activeTab === 'user' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {renderDropdown('studyDirector', extractOptions(possibleValues, 'studyDirector'), 'Study Director')}
            {renderDropdown('principalInvestigator', extractOptions(possibleValues, 'principalInvestigator'), 'Principal Investigator')}
          </div>
        )}
        {activeTab === 'study' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {renderDropdown('area', extractOptions(possibleValues, 'area'), 'Area')}
            {renderDropdown('studyType', extractOptions(possibleValues, 'studyType'), 'Study Type')}
            {renderDropdown('crop', extractOptions(possibleValues, 'crop'), 'Crop')}
            <div>
              <label className="block text-sm font-medium mb-1">Weighed out application</label>
              <input
                type="text"
                name="weighedOutApplication"
                value={form.weighedOutApplication}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700"
                placeholder="Weighed out application"
                list="weighedOutApplication-options"
                autoComplete="off"
              />
              <datalist id="weighedOutApplication-options">
                {extractOptions(possibleValues, 'weighedOutApplication').map(opt => (
                  <option key={opt} value={opt} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Field Phase Start</label>
              <input
                type="text"
                name="fieldPhaseStart"
                value={form.fieldPhaseStart}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700"
                placeholder="Field Phase Start"
                list="fieldPhaseStart-options"
                autoComplete="off"
              />
              <datalist id="fieldPhaseStart-options">
                {extractOptions(possibleValues, 'fieldPhaseStart').map(opt => (
                  <option key={opt} value={opt} />
                ))}
              </datalist>
            </div>
          </div>
        )}
      </div>

      {/* OK Button */}
      <div className="flex justify-end">
        <button
          type="button"
          className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded"
          onClick={() => onOk && onOk(form)}
        >
          OK
        </button>
      </div>
    </div>
  );
}